import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import UserKycView from "./UserKycView";
import { Loader, AlertCircle, X, Bell, BellOff } from "lucide-react";
import { adminUserService } from "../../../services/api/admin.user.service";
import { useNotification } from "@/contexts/notification/NotificationContext";
import { getFileExtensionByMime } from "@/lib/utils";
import { notificationService } from "../../../services/api/notification.service";

// Token utility function
const getAuthToken = () => {
  return localStorage.getItem("authToken");
};

// BlueDotIndicator component with improved styling
const BlueDotIndicator = ({ isNew, size = "md" }) => {
  if (!isNew) return null;

  const sizeClasses = {
    sm: "w-2 h-2",
    md: "w-3 h-3",
    lg: "w-4 h-4",
  };

  return (
    <span
      className={`${sizeClasses[size]} bg-blue-500 rounded-full flex-shrink-0 animate-pulse inline-block`}
      title="New KYC request"
    ></span>
  );
};

// RejectionCommentModal moved outside to fix focus issues
const RejectionCommentModal = ({
  isOpen,
  onClose,
  onReject,
  isSubmitting,
  rejectionError,
  initialComment
}) => {
  const textareaRef = useRef(null);
  const [localComment, setLocalComment] = useState("");
  const [clickCount, setClickCount] = useState(0);

  const isCommentValid = localComment.trim().length >= 3;

  useEffect(() => {
    if (isOpen) {
      setLocalComment(initialComment || "");
      setClickCount(0);
    }
  }, [isOpen, initialComment]);

  if (!isOpen) return null;

  const handleTextareaChange = (e) => {
    setLocalComment(e.target.value);
  };

  const handleTextareaClick = () => {
    setClickCount((prev) => prev + 1);
  };

  const handleReject = () => {
    onReject(localComment);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-5 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-lg font-semibold mb-4">Rejection Reason</h2>

        {rejectionError && (
          <div className="text-red-600 text-sm mb-3">
            {rejectionError}
          </div>
        )}

        <textarea
          ref={textareaRef}
          value={localComment}
          onChange={handleTextareaChange}
          onClick={handleTextareaClick}
          disabled={isSubmitting}
          placeholder="Explain the reason for rejection (minimum 3 characters)..."
          className={`w-full h-28 border rounded p-2 mb-1 focus:outline-none focus:ring-2 ${clickCount > 0 && !isCommentValid
            ? "border-red-500 focus:ring-red-500"
            : "border-gray-300 focus:ring-red-500"
            }`}
        />

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleReject}
            disabled={!isCommentValid || isSubmitting}
            className={`px-4 py-2 text-white rounded ${isCommentValid && !isSubmitting
              ? "bg-red-600 hover:bg-red-700"
              : "bg-red-300 cursor-not-allowed"
              }`}
          >
            {isSubmitting ? "Rejecting..." : "Reject"}
          </button>
        </div>
      </div>
    </div>
  );
};

const KycStatus = () => {
  const [kycRequests, setKycRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    limit: 10,
    total: 0,
  });
  const [filter, setFilter] = useState({
    status: "",
  });
  const [rejectionComment, setRejectionComment] = useState("");
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [selectedKycId, setSelectedKycId] = useState(null);

  // New state for details modal
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedKycDetails, setSelectedKycDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Enhanced error state management
  const [apiErrors, setApiErrors] = useState({
    fetchError: null,
    statusUpdateError: null,
    documentError: null,
    rejectionError: null,
  });
  const [loadingStates, setLoadingStates] = useState({
    statusUpdate: {},
    documentDownload: {},
    rejection: false,
    markAsRead: false,
  });

  // Local state for KYC notifications to handle real-time updates
  const [localKycNotificationCount, setLocalKycNotificationCount] = useState(0);
  const [showRedIndicator, setShowRedIndicator] = useState(false);

  // State to track which KYC requests are new/unread
  const [newKycRequests, setNewKycRequests] = useState(new Set());
  const [kycNotificationDetails, setKycNotificationDetails] = useState([]);

  // Notification context
  const {
    unreadCount,
    categorizedCounts,
    detailedUnreadCount,
    getCategoryCount,
    markCategoryAsRead,
    refreshNotifications,
  } = useNotification();

  // Updated status options to match the API
  const statusOptions = ["pending", "in-review", "verified", "rejected"];

  // Get KYC notification count and priority - use local state if available
  const getKycNotificationCount = () => {
    return (
      localKycNotificationCount ||
      categorizedCounts?.categories?.Kyc?.unread ||
      0
    );
  };

  const getKycNotificationPriority = () => {
    const kycData = categorizedCounts?.categories?.Kyc;
    if (!kycData) return "low";
    if (kycData.urgent > 0) return "urgent";
    if (kycData.high > 0) return "high";
    if (kycData.medium > 0) return "medium";
    return "low";
  };

  // Function to check if a KYC request is new
  const isKycRequestNew = (kycId) => {
    return newKycRequests.has(String(kycId));
  };

  // Function to get detailed notification info for KYC requests
  const fetchKycNotificationDetails = async () => {
    try {
      // Fetch detailed notification information from the notification service
      const response = await notificationService.getAdminNotifications("Kyc");

      if (response && response.notifications) {
        const newKycIds = new Set();
        const notificationDetails = [];

        response.notifications.forEach((notification) => {
          if (notification.referenceId && !notification.isRead) {
            const kycId = String(notification.referenceId);
            newKycIds.add(kycId);
            notificationDetails.push({
              ...notification,
              kycId: kycId
            });
          }
        });

        setNewKycRequests(newKycIds);
        setKycNotificationDetails(notificationDetails);

        // Update local notification count based on actual unread notifications
        setLocalKycNotificationCount(newKycIds.size);
        setShowRedIndicator(newKycIds.size > 0);
      }
    } catch (error) {
      console.error("Error fetching KYC notification details:", error);
      // Fallback to using categorized counts if detailed fetch fails
      const kycCount = categorizedCounts?.categories?.Kyc?.unread || 0;
      setLocalKycNotificationCount(kycCount);
      setShowRedIndicator(kycCount > 0);
    }
  };

  // Initialize notification count and red indicator
  useEffect(() => {
    const initializeNotifications = async () => {
      const kycCount = categorizedCounts?.categories?.Kyc?.unread || 0;
      setLocalKycNotificationCount(kycCount);
      setShowRedIndicator(kycCount > 0);

      // Always fetch detailed notification info
      await fetchKycNotificationDetails();
    };

    initializeNotifications();
  }, []); // Empty dependency - only run once on mount to prevent infinite loop

  // Removed 30-second auto-refresh interval to prevent repeated API calls

  useEffect(() => {
    fetchKycRequests();
  }, [pagination.page, filter.status]);

  // Enhanced error handling utility
  const handleApiError = (error, errorType) => {

    let errorMessage = "An unexpected error occurred";

    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;

      if (data && data.message) {
        errorMessage = data.message;
      } else if (data && data.error) {
        errorMessage = data.error;
      } else {
        switch (status) {
          case 400:
            errorMessage = "Invalid request. Please check your input.";
            break;
          case 401:
            errorMessage = "Authentication required. Please log in again.";
            break;
          case 403:
            errorMessage = "You don't have permission to perform this action.";
            break;
          case 404:
            errorMessage = "The requested resource was not found.";
            break;
          case 409:
            errorMessage = "Conflict occurred. The resource may already exist.";
            break;
          case 500:
            errorMessage = "Server error. Please try again later.";
            break;
          default:
            errorMessage = `Request failed with status ${status}`;
        }
      }
    } else if (error.request) {
      // Network error
      errorMessage = "Network error. Please check your internet connection.";
    } else if (error.message) {
      errorMessage = error.message;
    }

    setApiErrors((prev) => ({
      ...prev,
      [errorType]: errorMessage,
    }));

    // Auto-clear error after 10 seconds
    setTimeout(() => {
      setApiErrors((prev) => ({
        ...prev,
        [errorType]: null,
      }));
    }, 10000);
  };

  const fetchKycRequests = async () => {
    setLoading(true);
    setApiErrors((prev) => ({ ...prev, fetchError: null }));

    try {
      const token = getAuthToken();

      const response = await adminUserService.getKycRequests(
        pagination.page,
        pagination.limit,
        filter.status,
        token
      );

      if (response.status === "success") {
        setKycRequests(response.data.kycRequests);
        setPagination((prev) => ({
          ...prev,
          total: response.pagination.total,
          pages: response.pagination.pages,
        }));
        setError(null);
      } else {
        throw new Error(response.message || "Failed to fetch KYC requests");
      }
    } catch (err) {
      handleApiError(err, "fetchError");
      setError(err.message || "An error occurred while fetching KYC requests");
    } finally {
      setLoading(false);
    }
  };

  // Handle marking individual KYC as read with proper count management
  const handleMarkKycAsRead = async (kycId) => {
    const kycIdStr = String(kycId);

    if (!isKycRequestNew(kycIdStr)) {
      return;
    }

    try {
      // Find the specific notification for this KYC
      const kycNotification = kycNotificationDetails.find(
        notif => notif.kycId === kycIdStr
      );

      if (kycNotification) {
        // Mark specific notification as read
        await notificationService.markNotificationAsRead(kycNotification._id);
      }

      // Update local state to remove blue dot immediately
      setNewKycRequests((prev) => {
        const newSet = new Set(prev);
        newSet.delete(kycIdStr);
        return newSet;
      });

      // Update local notification count
      setLocalKycNotificationCount((prev) => {
        const newCount = Math.max(0, prev - 1);
        if (newCount === 0) {
          setShowRedIndicator(false);
        }
        return newCount;
      });

      // Refresh notifications context
      if (refreshNotifications) {
        await refreshNotifications();
      }
    } catch (error) {
      console.error("Failed to mark KYC notification as read:", error);
    }
  };

  // Handle marking KYC notifications as read with improved logic
  const handleMarkAsRead = async () => {
    if (getKycNotificationCount() === 0) return;

    setLoadingStates((prev) => ({ ...prev, markAsRead: true }));

    try {
      // Call the notification service to mark KYC category as read
      await notificationService.markCategoryAsRead("Kyc");

      // Update local state immediately for better UX
      setLocalKycNotificationCount(0);
      setShowRedIndicator(false);
      setNewKycRequests(new Set());

      // Refresh notifications context
      if (refreshNotifications) {
        await refreshNotifications();
      }

      // Also try the context method as backup
      if (markCategoryAsRead) {
        await markCategoryAsRead("Kyc");
      }
    } catch (error) {
      console.error("Failed to mark KYC notifications as read:", error);
      // Revert local state if API call failed
      const originalCount = categorizedCounts?.categories?.Kyc?.unread || 0;
      setLocalKycNotificationCount(originalCount);
      setShowRedIndicator(originalCount > 0);
      await fetchKycNotificationDetails();
    } finally {
      setLoadingStates((prev) => ({ ...prev, markAsRead: false }));
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= pagination.pages) {
      setPagination({ ...pagination, page: newPage });
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter({ ...filter, [name]: value });
    setPagination({ ...pagination, page: 1 });
  };

  // Enhanced document viewing with better error handling
  const handleViewDocument = async (requestId, documentType) => {
    setLoadingStates((prev) => ({
      ...prev,
      documentDownload: {
        ...prev.documentDownload,
        [`${requestId}-${documentType}`]: true,
      },
    }));

    setApiErrors((prev) => ({ ...prev, documentError: null }));

    try {
      const token = getAuthToken();

      const {
        blob,
        documentType: type,
        requestId: id,
      } = await adminUserService.downloadKycDocument(
        requestId,
        documentType,
        token
      );

      const url = URL.createObjectURL(blob);
      const extension = getFileExtensionByMime(blob.type);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${type}-${id}${extension}`;
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);

      // Auto-mark as read when document is viewed
      if (isKycRequestNew(requestId)) {
        await handleMarkKycAsRead(requestId);
      }
    } catch (err) {
      handleApiError(err, "documentError");
    } finally {
      setLoadingStates((prev) => ({
        ...prev,
        documentDownload: {
          ...prev.documentDownload,
          [`${requestId}-${documentType}`]: false,
        },
      }));
    }
  };

  // Enhanced status change with loading states
  const handleStatusChange = async (kycId, newStatus) => {
    if (newStatus === "rejected") {
      setSelectedKycId(kycId);
      setCommentModalOpen(true);
      return;
    }

    setLoadingStates((prev) => ({
      ...prev,
      statusUpdate: { ...prev.statusUpdate, [kycId]: true },
    }));

    setApiErrors((prev) => ({ ...prev, statusUpdateError: null }));

    const token = getAuthToken();

    try {
      if (newStatus === "verified") {
        await adminUserService.verifyKyc(kycId, token);
      } else if (newStatus === "in-review") {
        await adminUserService.markInReviewKyc(kycId, token);
      }

      // Clear rejection reason if status is changing from rejected to something else
      if (newStatus !== "rejected") {
        const updatedRequests = kycRequests.map((request) => {
          if (request._id === kycId) {
            return { ...request, rejectionReason: null };
          }
          return request;
        });
        setKycRequests(updatedRequests);
      }

      // Mark as read when status is changed
      if (isKycRequestNew(kycId)) {
        await handleMarkKycAsRead(kycId);
      }

      // Refresh the list after successful update
      await fetchKycRequests();
    } catch (err) {
      handleApiError(err, "statusUpdateError");
      console.error(`Error updating status to ${newStatus}:`, err);
    } finally {
      setLoadingStates((prev) => ({
        ...prev,
        statusUpdate: { ...prev.statusUpdate, [kycId]: false },
      }));
    }
  };

  // Enhanced rejection handling
  const handleRejectWithComment = async (comment) => {
    if (!selectedKycId || !comment?.trim()) return;

    setLoadingStates((prev) => ({ ...prev, rejection: true }));
    setApiErrors((prev) => ({ ...prev, rejectionError: null }));

    try {
      const token = getAuthToken();
      await adminUserService.rejectKycWithComment(
        selectedKycId,
        comment,
        token
      );

      // Mark as read when rejected
      if (isKycRequestNew(selectedKycId)) {
        await handleMarkKycAsRead(selectedKycId);
      }

      setCommentModalOpen(false);
      setRejectionComment("");
      setSelectedKycId(null);
      await fetchKycRequests();
    } catch (err) {
      handleApiError(err, "rejectionError");
    } finally {
      setLoadingStates((prev) => ({ ...prev, rejection: false }));
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "verified":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "in-review":
        return "bg-blue-100 text-blue-800";
      case "pending":
      default:
        return "bg-yellow-100 text-yellow-800";
    }
  };

  // Enhanced Error Display Component
  const ErrorAlert = ({ error, onClose }) => {
    if (!error) return null;

    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-start justify-between">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Error occurred</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-red-500 hover:text-red-700 ml-4"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  };


  return (
    <div className="container mx-auto p-4">
      {detailsModalOpen && selectedKycDetails ? (
        <>
          <div className="mb-4">
            <button
              onClick={() => {
                setDetailsModalOpen(false);
                setSelectedKycDetails(null);
              }}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded"
            >
              ← Back to List
            </button>
          </div>
          <UserKycView id={selectedKycDetails._id} />
        </>
      ) : (
        <>
          {/* Header with Title and Notification Indicator */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <h1 className="text-2xl font-bold">KYC Request Management</h1>
              {showRedIndicator && getKycNotificationCount() > 0 && (
                <div className="relative w-full sm:w-auto">
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 w-full sm:w-auto">
                    <div className="relative">
                      <Bell className="h-5 w-5 text-red-600" />
                      <div className="absolute -top-1 -right-1">
                        <div className="h-3 w-3 bg-red-500 rounded-full animate-pulse"></div>
                      </div>
                    </div>
                    <span className="text-red-800 text-sm font-medium">
                      {getKycNotificationCount()} new Pending notification
                      {getKycNotificationCount() > 1 ? "s" : ""}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Mark as Read Button - Responsive */}
            {showRedIndicator && getKycNotificationCount() > 0 && (
              <button
                onClick={handleMarkAsRead}
                disabled={loadingStates.markAsRead}
                className={`
                  flex items-center gap-2
                  px-3 py-2
                  rounded-lg text-sm font-medium
                  transition-all duration-200
                  w-full sm:w-auto
                  justify-center
                  ${loadingStates.markAsRead
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg"
                  }
                `}
                style={{ minWidth: "140px" }}
              >
                {loadingStates.markAsRead ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    <span className="hidden sm:inline">Marking as Read...</span>
                    <span className="sm:hidden">Loading...</span>
                  </>
                ) : (
                  <>
                    <BellOff className="h-4 w-4" />
                    <span className="hidden sm:inline">Mark All as Read</span>
                    <span className="sm:hidden">Mark Read</span>
                  </>
                )}
              </button>
            )}
          </div>

          {/* Enhanced Error Messages */}
          <ErrorAlert
            error={apiErrors.fetchError || error}
            onClose={() => {
              setError(null);
              setApiErrors((prev) => ({ ...prev, fetchError: null }));
            }}
          />

          <ErrorAlert
            error={apiErrors.statusUpdateError}
            onClose={() =>
              setApiErrors((prev) => ({ ...prev, statusUpdateError: null }))
            }
          />

          <ErrorAlert
            error={apiErrors.documentError}
            onClose={() =>
              setApiErrors((prev) => ({ ...prev, documentError: null }))
            }
          />

          {/* Filter section */}
          <div className="mb-6 p-4 bg-gray-50 rounded shadow">
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <label
                  htmlFor="status"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Filter by Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={filter.status}
                  onChange={handleFilterChange}
                  className="border border-gray-300 rounded-md p-2 w-full"
                >
                  <option value="">All Statuses</option>
                  {statusOptions.map((option) => (
                    <option key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={() => setFilter({ status: "" })}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded self-end"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Loading Spinner */}
          {loading && (
            <div className="flex justify-center items-center h-32">
              <Loader className="animate-spin text-indigo-500 mb-4" size={36} />
            </div>
          )}

          {/* KYC Requests Table */}
          {!loading && kycRequests.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="py-3 px-4 text-left">User</th>
                    <th className="py-3 px-4 text-left">Contact Info</th>
                    <th className="py-3 px-4 text-left">Verification Status</th>
                    <th className="py-3 px-4 text-left">Documents</th>
                    <th className="py-3 px-4 text-left">Submitted On</th>
                    <th className="py-3 px-4 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {kycRequests.map((request) => {
                    const isNew = isKycRequestNew(request._id);
                    return (
                      <tr
                        key={request._id}
                        className={`border-t border-gray-200 hover:bg-gray-50 transition-colors ${isNew
                          ? "bg-blue-50 border-l-4 border-l-blue-500"
                          : ""
                          }`}
                      >
                        {/* User column with blue dot indicator */}
                        <td className="py-3 px-4">
                          {request.user ? (
                            <div>
                              <div className="font-medium flex items-center gap-2">
                                <BlueDotIndicator
                                  isNew={isNew}
                                  size="sm"
                                />
                                <span>{request.user.name}</span>
                                {isNew && (
                                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full ml-2">
                                    New
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500">
                                ID: {request.user.id}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-500">
                              User data unavailable
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {request.emailAddress ||
                            (request.user && request.user.email) ||
                            "N/A"}
                          <br />
                          {request.mobileNumber ||
                            (request.user && request.user.mobileNo) ||
                            "N/A"}
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(
                              request.verificationStatus
                            )}`}
                          >
                            {request.verificationStatus || "pending"}
                          </span>
                          {/* Only show rejection reason if status is rejected */}
                          {request.verificationStatus === "rejected" &&
                            request.rejectionReason && (
                              <div className="text-xs text-red-600 mt-1">
                                Reason: {request.rejectionReason}
                              </div>
                            )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-col space-y-1">
                            <button
                              onClick={() =>
                                handleViewDocument(request._id, "panCard")
                              }
                              disabled={
                                !request.documents?.panCard?.hasData ||
                                loadingStates.documentDownload[
                                `${request._id}-panCard`
                                ]
                              }
                              className={`text-sm flex items-center ${request.documents?.panCard?.hasData &&
                                !loadingStates.documentDownload[
                                `${request._id}-panCard`
                                ]
                                ? "text-blue-600 hover:underline"
                                : "text-gray-400 cursor-not-allowed"
                                }`}
                            >
                              {loadingStates.documentDownload[
                                `${request._id}-panCard`
                              ] && (
                                  <Loader className="animate-spin h-3 w-3 mr-1" />
                                )}
                              PAN Card{" "}
                              {request.documents?.panCard?.hasData ? "✓" : "✕"}
                            </button>

                            {request.documents?.aadhaarFront?.hasData ||
                            request.documents?.aadhaarBack?.hasData ? (
                              <div className="flex flex-col space-y-1">
                                {request.documents?.aadhaarFront?.hasData && (
                                  <button
                                    onClick={() =>
                                      handleViewDocument(
                                        request._id,
                                        "aadhaarFront"
                                      )
                                    }
                                    disabled={
                                      loadingStates.documentDownload[
                                      `${request._id}-aadhaarFront`
                                      ]
                                    }
                                    className={`text-sm flex items-center ${!loadingStates.documentDownload[
                                      `${request._id}-aadhaarFront`
                                      ]
                                      ? "text-blue-600 hover:underline"
                                      : "text-gray-400 cursor-not-allowed"
                                      }`}
                                  >
                                    {loadingStates.documentDownload[
                                      `${request._id}-aadhaarFront`
                                    ] && (
                                        <Loader className="animate-spin h-3 w-3 mr-1" />
                                      )}
                                    Aadhaar Front ✓
                                  </button>
                                )}
                                {request.documents?.aadhaarBack?.hasData && (
                                  <button
                                    onClick={() =>
                                      handleViewDocument(
                                        request._id,
                                        "aadhaarBack"
                                      )
                                    }
                                    disabled={
                                      loadingStates.documentDownload[
                                      `${request._id}-aadhaarBack`
                                      ]
                                    }
                                    className={`text-sm flex items-center ${!loadingStates.documentDownload[
                                      `${request._id}-aadhaarBack`
                                      ]
                                      ? "text-blue-600 hover:underline"
                                      : "text-gray-400 cursor-not-allowed"
                                      }`}
                                  >
                                    {loadingStates.documentDownload[
                                      `${request._id}-aadhaarBack`
                                    ] && (
                                        <Loader className="animate-spin h-3 w-3 mr-1" />
                                      )}
                                    Aadhaar Back ✓
                                  </button>
                                )}
                              </div>
                            ) : (
                              <button
                                onClick={() =>
                                  handleViewDocument(request._id, "aadhaarCard")
                                }
                                disabled={
                                  !request.documents?.aadhaarCard?.hasData ||
                                  loadingStates.documentDownload[
                                  `${request._id}-aadhaarCard`
                                  ]
                                }
                                className={`text-sm flex items-center ${request.documents?.aadhaarCard?.hasData &&
                                  !loadingStates.documentDownload[
                                  `${request._id}-aadhaarCard`
                                  ]
                                  ? "text-blue-600 hover:underline"
                                  : "text-gray-400 cursor-not-allowed"
                                  }`}
                              >
                                {loadingStates.documentDownload[
                                  `${request._id}-aadhaarCard`
                                ] && (
                                    <Loader className="animate-spin h-3 w-3 mr-1" />
                                  )}
                                Aadhaar Card{" "}
                                {request.documents?.aadhaarCard?.hasData
                                  ? "✓"
                                  : "✕"}
                              </button>
                            )}

                            <button
                              onClick={() =>
                                handleViewDocument(request._id, "bankDocuments")
                              }
                              disabled={
                                !request.documents?.bankDocuments?.hasData ||
                                loadingStates.documentDownload[
                                `${request._id}-bankDocuments`
                                ]
                              }
                              className={`text-sm flex items-center ${request.documents?.bankDocuments?.hasData &&
                                !loadingStates.documentDownload[
                                `${request._id}-bankDocuments`
                                ]
                                ? "text-blue-600 hover:underline"
                                : "text-gray-400 cursor-not-allowed"
                                }`}
                            >
                              {loadingStates.documentDownload[
                                `${request._id}-bankDocuments`
                              ] && (
                                  <Loader className="animate-spin h-3 w-3 mr-1" />
                                )}
                              Bank Documents{" "}
                              {request.documents?.bankDocuments?.hasData
                                ? "✓"
                                : "✕"}
                            </button>

                            <button
                              onClick={() =>
                                handleViewDocument(request._id, "dematDocuments")
                              }
                              disabled={
                                !request.documents?.dematDocuments?.hasData ||
                                loadingStates.documentDownload[
                                `${request._id}-dematDocuments`
                                ]
                              }
                              className={`text-sm flex items-center ${request.documents?.dematDocuments?.hasData &&
                                !loadingStates.documentDownload[
                                `${request._id}-dematDocuments`
                                ]
                                ? "text-blue-600 hover:underline"
                                : "text-gray-400 cursor-not-allowed"
                                }`}
                            >
                              {loadingStates.documentDownload[
                                `${request._id}-dematDocuments`
                              ] && (
                                  <Loader className="animate-spin h-3 w-3 mr-1" />
                                )}
                              Demat Documents{" "}
                              {request.documents?.dematDocuments?.hasData
                                ? "✓"
                                : "✕"}
                            </button>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {new Date(request.createdAt).toLocaleDateString()}
                          {request.verificationStatus === "verified" &&
                            request.verifiedAt && (
                              <div className="text-xs text-green-600 mt-1">
                                Verified:{" "}
                                {new Date(
                                  request.verifiedAt
                                ).toLocaleDateString()}
                              </div>
                            )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-col space-y-2">
                            {isNew && (
                              <button
                                onClick={() => handleMarkKycAsRead(request._id)}
                                className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded hover:bg-green-200"
                                title="Mark as read"
                              >
                                Mark Read
                              </button>
                            )}

                            <select
                              value={request.verificationStatus || "pending"}
                              onChange={(e) =>
                                handleStatusChange(request._id, e.target.value)
                              }
                              disabled={loadingStates.statusUpdate[request._id]}
                              className="border border-gray-300 rounded px-2 py-1 text-sm w-full disabled:opacity-50"
                            >
                              {statusOptions.map((option) => (
                                <option key={option} value={option}>
                                  {option.charAt(0).toUpperCase() +
                                    option.slice(1)}
                                </option>
                              ))}
                            </select>

                            {loadingStates.statusUpdate[request._id] && (
                              <div className="flex items-center text-sm text-blue-600">
                                <Loader className="animate-spin h-3 w-3 mr-1" />
                                Updating...
                              </div>
                            )}

                            <button
                              onClick={() => {
                                setSelectedKycDetails(request);
                                setDetailsModalOpen(true);
                              }}
                              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm"
                            >
                              View Details
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            !loading && (
              <div className="text-center py-10 bg-gray-50 rounded">
                <p className="text-gray-500">
                  No KYC requests found with the selected filters.
                </p>
              </div>
            )
          )}


          {/* Pagination */}
          {!loading && pagination.pages > 1 && (
            <div className="flex justify-between items-center mt-6">
              <div className="text-sm text-gray-700">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
                of {pagination.total} results
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.page === 1}
                  className={`px-3 py-1 rounded ${pagination.page === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                    }`}
                >
                  First
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className={`px-3 py-1 rounded ${pagination.page === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                    }`}
                >
                  Prev
                </button>
                {Array.from(
                  { length: Math.min(5, pagination.pages) },
                  (_, i) => {
                    // Show pages around the current page
                    const start = Math.max(1, pagination.page - 2);
                    const end = Math.min(pagination.pages, start + 4);
                    const adjustedStart = end - 4 < 1 ? 1 : end - 4;
                    const pageNum =
                      i + (pagination.pages <= 5 ? 1 : adjustedStart);

                    if (pageNum <= pagination.pages) {
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-1 rounded ${pagination.page === pageNum
                            ? "bg-blue-500 text-white"
                            : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                    return null;
                  }
                )}
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className={`px-3 py-1 rounded ${pagination.page === pagination.pages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                    }`}
                >
                  Next
                </button>
                <button
                  onClick={() => handlePageChange(pagination.pages)}
                  disabled={pagination.page === pagination.pages}
                  className={`px-3 py-1 rounded ${pagination.page === pagination.pages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                    }`}
                >
                  Last
                </button>
              </div>
            </div>
          )}

          {/* Fixed Rejection Comment Modal */}
          <RejectionCommentModal
            isOpen={commentModalOpen}
            onClose={() => {
              setCommentModalOpen(false);
              setRejectionComment("");
              setSelectedKycId(null);
              setApiErrors((prev) => ({ ...prev, rejectionError: null }));
            }}
            onReject={handleRejectWithComment}
            isSubmitting={loadingStates.rejection}
            rejectionError={apiErrors.rejectionError}
            initialComment={rejectionComment}
          />
        </>
      )}
    </div>
  );
};

export default KycStatus;
